if (!customElements.get('gjl-monster-header')) {
  // Section rendering may mount the drawer badge after the confirmed add signal.
  // Keep a brief in-memory reaction; never infer purchase success from a click.
  let latestConfirmedCartReaction = null;
  customElements.define('gjl-monster-header', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.timers = new Set();
      this.observers = [];
      this.queueTimers = new Set();
      this.unsubscribers = [];
      this.pending = null;
      this.activeReaction = null;
      this.lastAmbient = 0;
      this.lastInteraction = 0;
      this.lastReaction = { name: '', at: 0 };
      this.pointerHeld = false;
      this.galleryPlayed = false;
      this.productPlayed = false;
      this.hoverCount = 0;
      this.playIndex = 0;
      this.lastPlayAt = 0;
      this.context = this.dataset.mascotContext === 'cart' ? 'cart' : 'header';
      this.drawer = this.context === 'cart' ? this.closest('cart-drawer') : null;
      this.bundlerReady = false;
      this.cartCount = this.parseCount(this.dataset.cartCount);
      this.home = this.querySelector('.gjl-monster-home');
      this.playButton = this.querySelector('.gjl-monster-play');
      this.message = this.querySelector('[data-gjl-mascot-message]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!this.home && !this.playButton) return;
      const options = { signal: this.controller.signal };
      const stopAmbient = () => {
        this.lastInteraction = Date.now();
        if (!this.activeReaction) this.stopMotion();
      };
      this.motion.addEventListener('change', () => {
        this.stopMotion();
        this.clearQueue();
      }, options);
      document.addEventListener('visibilitychange', () => {
        this.stopMotion();
        if (document.hidden) this.clearQueue();
      }, options);
      document.addEventListener('pointerdown', () => {
        this.pointerHeld = true;
        stopAmbient();
      }, options);
      for (const name of ['pointerup', 'pointercancel']) {
        document.addEventListener(name, () => {
          this.pointerHeld = false;
          this.queueFlush();
        }, options);
      }
      document.addEventListener('keydown', stopAmbient, options);
      document.addEventListener('input', stopAmbient, options);
      document.addEventListener('keyup', () => this.queueFlush(), options);
      document.addEventListener('focusout', () => this.queueFlush(), options);
      document.addEventListener('toggle', (event) => {
        if (event.target instanceof HTMLDetailsElement && event.target.open) this.stopMotion();
        this.queueFlush();
      }, { capture: true, signal: this.controller.signal });
      this.home?.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        const name = this.hoverCount % 2 === 0 ? 'twist' : 'puff';
        if (this.playAmbient(name)) this.hoverCount += 1;
      }, options);
      this.home?.addEventListener('focus', () => this.playAmbient('twist'), options);
      this.home?.addEventListener('blur', () => {
        if (!this.activeReaction) this.stopMotion();
      }, options);
      this.playButton?.addEventListener('click', () => this.playExplicit(), options);
      this.playButton?.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.playAmbient('twist');
      }, options);
      document.addEventListener('submit', (event) => {
        if (event.submitter?.matches('[name="checkout"]')) this.recordCheckout();
      }, { capture: true, signal: this.controller.signal });
      document.addEventListener('click', (event) => this.inspectCheckoutLink(event), options);
      document.addEventListener('bundler:total_cart_values', (event) => {
        const cart = event.detail?.original_cart_object;
        if (!cart || cart.errors || cart.status) return;
        const count = this.parseCount(cart.item_count);
        if (count === null) return;
        if (!this.bundlerReady) {
          this.bundlerReady = true;
          this.cartCount = count;
          return;
        }
        this.updateCartCount(count);
      }, options);
      this.connectCartEvents(0);
      if (this.context === 'header') this.checkPaidReturn();
      else this.setupDrawerContext();
      this.schedule(() => this.setupContext(), 700);
    }

    schedule(callback, delay) {
      const timer = window.setTimeout(() => {
        this.timers.delete(timer);
        this.queueTimers.delete(timer);
        if (this.isConnected && this.controller) callback();
      }, delay);
      this.timers.add(timer);
      return timer;
    }

    cancelTimer(timer) {
      if (!timer) return;
      clearTimeout(timer);
      this.timers.delete(timer);
      this.queueTimers.delete(timer);
    }

    readPreference(key) {
      try { return sessionStorage.getItem(key); } catch (_) { return null; }
    }

    savePreference(key, value) {
      try { sessionStorage.setItem(key, value); } catch (_) {}
    }

    removePreference(key) {
      try { sessionStorage.removeItem(key); } catch (_) {}
    }

    parseCount(value) {
      if (value === undefined || value === null || value === '') return null;
      const count = Number(value);
      return Number.isInteger(count) && count >= 0 ? count : null;
    }

    connectCartEvents(attempt) {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          try { this.onCartUpdate(event); } catch (_) {}
        }));
      } else if (attempt < 3) {
        this.schedule(() => this.connectCartEvents(attempt + 1), 500);
      }
    }

    onCartUpdate(event) {
      const cart = event?.cartData;
      if (!cart || cart.errors || cart.status) return;
      if (event.source === 'product-form') {
        const added = (cart.id && Number(cart.quantity) > 0) ||
          (Array.isArray(cart.items) && cart.items.some((item) => item.id && Number(item.quantity) > 0));
        if (!added) return;
        const count = this.parseCount(cart.item_count);
        this.cartCount = count === null ? Math.max(this.cartCount || 0, 1) : count;
        this.cartReaction('grind');
      } else if (event.source === 'cart-items' || event.source === 'gjl-cart-drawer') {
        const count = this.parseCount(cart.item_count);
        if (count !== null) this.updateCartCount(count);
      }
    }

    updateCartCount(count) {
      const previous = this.cartCount;
      this.cartCount = count;
      if (previous === null || count === previous) return;
      if (count > previous) {
        this.cartReaction('grind');
      } else if (previous > 0 && count === 0) {
        if (this.pending?.name === 'grind') this.clearQueue();
        if (this.activeReaction?.name === 'grind') this.stopMotion();
        this.cartReaction('sad');
      }
    }

    cartReaction(name) {
      latestConfirmedCartReaction = { name, at: Date.now() };
      return this.requestReaction(name);
    }

    setupDrawerContext() {
      if (!this.drawer) return;
      const open = () => this.drawer.classList.contains('active');
      let wasOpen = open();
      const greet = () => {
        if (!open() || document.hidden || this.motion.matches) return;
        const recent = latestConfirmedCartReaction;
        if (recent && Date.now() - recent.at < 3000) {
          this.requestReaction(recent.name);
        } else if (!this.pending && !this.activeReaction) {
          // A warm greeting says "your picks are here", not "payment complete".
          this.playAmbient(this.cartCount > 0 ? 'joy' : 'hello');
        }
      };
      this.drawerObserver = new MutationObserver(() => {
        const isOpen = open();
        if (isOpen && !wasOpen) this.schedule(greet, 220);
        if (!isOpen && wasOpen) this.stopMotion();
        wasOpen = isOpen;
        this.queueFlush();
      });
      this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      this.observers.push(this.drawerObserver);
      const recent = latestConfirmedCartReaction;
      if (recent && Date.now() - recent.at < 3000) this.requestReaction(recent.name);
      if (wasOpen) this.schedule(greet, 220);
    }

    playExplicit() {
      if (document.hidden || this.pointerHeld || this.pending || this.activeReaction) return false;
      const now = Date.now();
      if (now - this.lastPlayAt < 300) return false;
      if (!this.motion.matches && !this.isAvailable()) return false;
      const name = ['twist', 'grind', 'joy'][this.playIndex % 3];
      this.playIndex += 1;
      this.lastPlayAt = now;
      this.lastInteraction = now;
      if (this.message) {
        this.message.textContent = { twist: 'A little mischief.', grind: 'Give it a grind.', joy: 'That’s your kind of joy.' }[name];
        this.setAttribute('data-emotion', name);
        this.cancelTimer(this.messageTimer);
        this.messageTimer = this.schedule(() => {
          this.removeAttribute('data-emotion');
          this.message.textContent = '';
          this.messageTimer = null;
        }, 2400);
      }
      if (!this.motion.matches) this.startMotion(name);
      return true;
    }

    priority(name) {
      return { joy: 3, grind: 2, sad: 1 }[name] || 0;
    }

    requestReaction(name, onStart) {
      if (this.motion.matches || document.hidden) return false;
      const now = Date.now();
      if (name === this.lastReaction.name && now - this.lastReaction.at < 1200) return false;
      if (this.pending && this.priority(this.pending.name) > this.priority(name)) return false;
      this.clearQueue();
      this.lastReaction = { name, at: now };
      this.pending = { name, onStart, readyAt: now + 180, expiresAt: now + 15000 };
      if (!this.activeReaction || this.priority(name) > this.priority(this.activeReaction.name)) this.stopMotion();
      const pending = this.pending;
      for (const delay of [180, 550, 1200, 2500, 5000, 10000, 15010]) {
        this.queueTimers.add(this.schedule(() => {
          if (this.pending === pending) this.flushQueue();
        }, delay));
      }
      if (typeof MutationObserver !== 'undefined' && document.body) {
        this.queueObserver = new MutationObserver((records) => {
          if (records.some((record) => !this.contains(record.target))) this.queueFlush();
        });
        this.queueObserver.observe(document.body, {
          subtree: true, childList: true, attributes: true,
          attributeFilter: ['class', 'open', 'hidden', 'aria-hidden', 'style']
        });
      }
      return true;
    }

    queueFlush() {
      if (!this.pending || this.flushTimer) return;
      this.flushTimer = this.schedule(() => {
        this.flushTimer = null;
        this.flushQueue();
      }, 100);
      this.queueTimers.add(this.flushTimer);
    }

    clearQueue() {
      for (const timer of this.queueTimers || []) this.cancelTimer(timer);
      this.queueTimers?.clear();
      this.queueObserver?.disconnect();
      this.queueObserver = null;
      this.flushTimer = null;
      this.pending = null;
    }

    flushQueue() {
      const pending = this.pending;
      if (!pending) return;
      if (Date.now() >= pending.expiresAt || this.motion.matches || document.hidden) {
        this.clearQueue();
        return;
      }
      if (Date.now() < pending.readyAt) return;
      if (!this.isAvailable() || this.hasAttribute('data-motion')) return;
      this.clearQueue();
      this.startMotion(pending.name, pending);
      pending.onStart?.();
    }

    isAvailable() {
      if (this.motion.matches || document.hidden || this.pointerHeld) return false;
      if (this.context === 'cart' && (!this.drawer || !this.drawer.classList.contains('active'))) return false;
      const rect = this.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return false;
      if (document.activeElement?.matches('input,textarea,select,[contenteditable="true"]')) return false;
      const blockers = document.querySelectorAll('.header details[open], .cart-drawer.active, cart-drawer.active, .cart-notification.active, #bndlr-discount-message, dialog[open], [role="dialog"][aria-modal="true"]:not([hidden]):not([aria-hidden="true"])');
      return !Array.from(blockers).some((element) => {
        // The cart badge is inside its own dialog, so that dialog is its stage.
        // Other dialogs still suppress its animation while shoppers act there.
        if (this.context === 'cart' && (element === this.drawer || element === this.drawer?.querySelector('.drawer__inner'))) return false;
        const box = element.getBoundingClientRect();
        const style = getComputedStyle(element);
        return !element.closest('details:not([open])') && box.width > 8 && box.height > 8 &&
          box.bottom > 0 && box.top < window.innerHeight && box.right > 0 && box.left < window.innerWidth &&
          style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0';
      });
    }

    startMotion(name, reaction = null) {
      this.stopMotion();
      if (reaction) {
        this.cancelTimer(this.messageTimer);
        this.messageTimer = null;
        this.removeAttribute('data-emotion');
        if (this.message) this.message.textContent = '';
      }
      this.activeReaction = reaction;
      this.setAttribute('data-motion', name);
      const duration = { hello: 800, twist: 700, puff: 1100, look: 700, grind: 1800, sad: 1200, joy: 1200 }[name];
      this.motionTimer = this.schedule(() => {
        this.stopMotion();
        this.flushQueue();
      }, duration + 50);
    }

    stopMotion() {
      this.cancelTimer(this.motionTimer);
      this.motionTimer = null;
      this.activeReaction = null;
      this.removeAttribute('data-motion');
    }

    playAmbient(name, automatic = false) {
      if (!this.isAvailable() || this.pending || this.hasAttribute('data-motion')) return false;
      const now = Date.now();
      if (now - this.lastAmbient < 8000 || (automatic && now - this.lastInteraction < 2500)) return false;
      this.lastAmbient = now;
      this.startMotion(name);
      return true;
    }

    setupContext() {
      if (this.context === 'cart') return;
      if (this.dataset.pageType === 'index' && !this.readPreference('gjl-monster-greeted-v3')) {
        if (this.playAmbient('hello', true)) this.savePreference('gjl-monster-greeted-v3', '1');
      }
      if (this.dataset.pageType !== 'product') return;
      this.schedule(() => this.productMoment(0), 8000);
      const galleryChanged = () => {
        if (!this.galleryPlayed && this.playAmbient('look')) this.galleryPlayed = true;
      };
      for (const slider of document.querySelectorAll('media-gallery slider-component')) {
        slider.addEventListener('slideChanged', galleryChanged, { signal: this.controller.signal });
      }
      for (const gallery of document.querySelectorAll('media-gallery')) {
        let activeMedia = gallery.querySelector('[data-media-id].is-active')?.getAttribute('data-media-id');
        const observer = new MutationObserver(() => {
          const nextMedia = gallery.querySelector('[data-media-id].is-active')?.getAttribute('data-media-id');
          if (!nextMedia || nextMedia === activeMedia) return;
          activeMedia = nextMedia;
          galleryChanged();
        });
        observer.observe(gallery, { subtree: true, attributes: true, attributeFilter: ['class'] });
        this.observers.push(observer);
      }
    }

    productMoment(attempt) {
      if (this.productPlayed || this.motion.matches) return;
      if (this.playAmbient('puff', true)) this.productPlayed = true;
      else if (attempt < 2) this.schedule(() => this.productMoment(attempt + 1), 5000);
    }

    inspectCheckoutLink(event) {
      const link = event.target.closest?.('a[href]');
      if (!link || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      try {
        const url = new URL(link.getAttribute('href'), window.location.href);
        if (url.origin === window.location.origin && /\/checkout\/?$/.test(url.pathname)) this.recordCheckout();
      } catch (_) {}
    }

    recordCheckout() {
      if (this.context !== 'header') return;
      if (this.dataset.customerPresent !== 'true') return;
      this.savePreference('gjl-monster-checkout-v3', JSON.stringify({
        startedAt: Date.now(), baselineOrderId: this.dataset.lastOrderId || ''
      }));
    }

    checkPaidReturn() {
      const key = 'gjl-monster-checkout-v3';
      let marker;
      try { marker = JSON.parse(this.readPreference(key)); } catch (_) { this.removePreference(key); return; }
      if (!marker) return;
      const now = Date.now();
      const startedAt = Number(marker.startedAt);
      if (!Number.isFinite(startedAt) || startedAt > now || now - startedAt > 7200000) {
        this.removePreference(key);
        return;
      }
      if (this.dataset.customerPresent !== 'true') return;
      const orderId = this.dataset.paidOrderId;
      const paidAt = Number(this.dataset.paidOrderAt);
      if (!orderId || !/^\d+$/.test(orderId) || orderId === String(marker.baselineOrderId || '') || !Number.isFinite(paidAt) || paidAt < startedAt - 30000 || paidAt > now + 30000) return;
      const consume = () => {
        this.savePreference('gjl-monster-celebrated-v3', orderId);
        this.removePreference(key);
      };
      if (this.motion.matches || this.readPreference('gjl-monster-celebrated-v3') === orderId) { consume(); return; }
      this.requestReaction('joy', consume);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.clearQueue();
      this.stopMotion();
      this.removeAttribute('data-emotion');
      if (this.message) this.message.textContent = '';
      for (const timer of this.timers || []) clearTimeout(timer);
      this.timers?.clear();
      for (const observer of this.observers || []) observer.disconnect();
      for (const unsubscribe of this.unsubscribers || []) unsubscribe();
      this.unsubscribers = [];
    }
  });
}
